import AuthenticationContext from "config/context/authenticationContext";
import GuiContext from "config/context/guiContext";
import PluginsContext from "config/context/pluginsContext";
import { useCallback, useContext, useState } from "react";
import { testPermissionName, testPrivateKey } from "utils/validations";

const initialServerStatus = {
    response: null,
    loading: false,
    error: null,
}

export const useSetAuthKey = ({ permissionName, privateKey }) => {
    const [response, setResponse] = useState(initialServerStatus.response);
    const [loading, setLoading] = useState(initialServerStatus.loading);
    const [error, setError] = useState(initialServerStatus.error);
    const { updateAuthKey } = useContext(AuthenticationContext);
    const { getActiveByAccountName } = useContext(PluginsContext);

    const {
        selected: { selectedAccount, selectedServer },
        URL,
        userData: { guiname }
    } = useContext(GuiContext);

    const isActivePlugin = getActiveByAccountName(guiname)?.active?.includes(7);

    const setAuthKey = async () => {

        if (!testPermissionName(permissionName)) {
            setError('Invalid permission name!');
            return;
        }

        if (!testPrivateKey(privateKey)) {
            setError('Invalid private key!');
            return;
        }

        setError(null);

        if (!isActivePlugin) {
            setResponse({ status: true });
            return;
        };

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/account/set_auth_key`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount,
                    permission_name: permissionName,
                    permission_private_key: privateKey,
                    server_url: selectedServer
                }),
            });

            if (!res.ok) {
                throw new Error('Failed to set authorization key');
            }

            const data = await res.json();
            setResponse(data);
            // Set authKey to context & localStorage
            updateAuthKey(permissionName, true, data?.expiration);
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        setAuthKey,
        resetServerStatus
    };
}

export const useClearAuthKeys = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { dropAuthKeys } = useContext(AuthenticationContext);

    const {
        URL,
    } = useContext(GuiContext);

    const clearAuthKeys = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${URL}/api/account/clear_auth_keys`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error('Failed to clear authorization keys');
            }

            const data = await res.json();
            setResponse(data);
            // Remove authKeys from context & localStorage
            dropAuthKeys();
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        clearAuthKeys,
        resetServerStatus
    };
};

export const useGenerateKeypair = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const {
        URL,
    } = useContext(GuiContext);

    const getKeypair = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${URL}/api/account/generate_key`, {
                method: 'GET',
            });

            if (!res.ok) {
                throw new Error('Failed to get keypair!');
            }

            const data = await res.json();
            setResponse(data);
        } catch (err) {
            setError(err.message || 'An error occurred!');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        getKeypair,
        resetServerStatus
    };
}